/**
 * THE ONE "THIS SONG IS PLAYING" VISUAL, for every playback surface in the app.
 *
 * ▮▯▮▮ — four animated bars, BESIDE the play/pause control rather than inside it.
 *
 * WHAT IT REPLACES. Every song surface drew its own playing state as a static Pause
 * glyph: the library row, the playlist track row, the Home page's chart rows and cards,
 * the queue panel. Meanwhile the music bar — the Studio's transport — showed an animated
 * waveform. So the app had two vocabularies for one fact, and the one people recognise
 * as "playing" appeared on exactly one screen. This is that waveform, at the size a row
 * has room for, so the same thing looks the same everywhere.
 *
 * NOT A SECOND STATE, and this is the whole of the design. It takes `playing` as a prop
 * and holds nothing: every caller passes `usePlayer().isPlaying(song.id)`, which is
 * `songId === id && !paused` read straight from playerStore. So it appears for the
 * currently playing song only, it goes when playback pauses or stops, and it moves to
 * the next row when the queue advances — not because anything here watches for that, but
 * because there is one piece of state and this is a view of it. There is no local
 * animation clock either: the pulse is a CSS animation on the bars.
 *
 * SIMULATED, exactly as much as the rest of the player is: the bar heights come from a
 * seeded PRNG keyed on the song id (lib/waveform.indicatorHeights), because there is no
 * audio to analyse — Song.audioUrl is null throughout. They are the first four bars of
 * the same shape the transport draws for that song, rescaled, so a song looks like
 * itself on every surface.
 *
 * IT IS NOT THE PLAY BUTTON'S GLYPH, and that is a correction (Batch 6 §13). It used to be
 * drawn INSIDE each play/pause control, replacing the Pause glyph while the clock ran — so
 * the app's most ordinary control stopped looking like a play/pause button on exactly the
 * surfaces where it mattered, and someone who wanted to pause had to recognise a waveform as
 * a pause target. §13 is explicit about it: "The normal Play/Pause button should remain a
 * NORMAL play/pause button. Do NOT animate the play button itself as a waveform."
 *
 * So every control now draws a plain Play or Pause glyph, exactly as it did before the
 * indicator existed, and this component sits BESIDE it as a separate element — which is the
 * arrangement the Studio's transport has always used (see studio/TransportBar: a normal
 * button, and a Waveform of its own further along the row). One vocabulary, and the
 * play/pause button is left alone.
 */
import Waveform from './Waveform';
import { indicatorHeights, INDICATOR_MAX_PX } from '../../lib/waveform';
import { c } from '../../styles/theme';

export interface PlayingIndicatorProps {
  /**
   * Seeds the bar shape. The song's id at every call site.
   *
   * A seed rather than a Song, so a surface holding only an id (the queue panel's rows
   * once, and anything future) can draw one without resolving the record first.
   */
  seed: string;
  /**
   * True when the clock is advancing for THIS song.
   *
   * Always `usePlayer().isPlaying(song.id)`. Taken as a prop rather than read from the
   * store here for the reason MiniPlayer states: a component that subscribes to playback
   * is a component that can be mounted somewhere it should not follow, and every song
   * surface already has the predicate in hand.
   */
  playing: boolean;
  /**
   * Bar colour. Defaults to the accent, which is what every current surface wants.
   *
   * IT USED TO DEFAULT TO `#1A1300`, the on-accent ink, because the indicator was drawn
   * inside an accent-filled circle where accent bars would have been invisible. Now that
   * it sits beside the control on the surface behind it (§13), the accent is the reading
   * that has contrast — it measures well clear of the 3:1 non-text threshold on all four
   * surfaces, see styles/contrast.test.ts. The prop stays for any surface that later draws
   * the bars on a saturated fill.
   */
  color?: string;
}

export default function PlayingIndicator({ seed, playing, color = c.accent }: PlayingIndicatorProps) {
  return (
    <Waveform
      /*
        Layout only, and it has to be a class rather than an inline style: the indicator is
        a fixed-width item on flex rows that must not squeeze it, and `flex-shrink: 0` on a
        component this widely used belongs in one place. See .lyx-playing in global.css.
      */
      className="lyx-playing"
      heights={indicatorHeights(seed)}
      playing={playing}
      barWidth={2.5}
      // The tallest bar, so the row of bars is bottom-aligned in a box exactly its own
      // height — a taller box would sit the group off-centre against the control beside it.
      height={INDICATOR_MAX_PX}
      color={color}
    />
  );
}
